import {openPatientCharacteristicsDialog} from '@/modules/patients/components/dialogs/util'
import {Patient} from '@cypro/engine/schema'
import {bsaFormulasOptions, ibwFormulasOptions} from '@/shared/constants'
import {Box, Button, Text, VStack} from '@chakra-ui/react'
import {SelectItem} from '@cypro/ui/components/form/select'
import {DataList} from '@cypro/ui/components/ui/data-list'
import {DataSection} from '@cypro/ui/components/ui/data-section'
import {multipleDigitsNumberFormatter} from '@cypro/ui/utils'
import {useCan} from '@/shared/hooks/use-permissions'
import {useTranslate} from '@tolgee/react'
import {useMemo} from 'react'

export function Characteristics({patient}: {patient: Patient}) {
  const {t} = useTranslate('common')
  const canEditPatient = useCan('patients.edit')

  const latest = patient.characteristics?.[0]

  const items = useMemo(
    () => [
      {
        label: t('characteristics.height'),
        value: latest?.height ? multipleDigitsNumberFormatter.format(latest?.height) : '-',
      },
      {
        label: t('characteristics.weight'),
        value: latest?.weight ? multipleDigitsNumberFormatter.format(latest?.weight) : '-',
      },
      {
        label: t('characteristics.bsa'),
        value: latest?.bsa ? multipleDigitsNumberFormatter.format(latest?.bsa) : '-',
      },
      {
        label: t('characteristics.bsaFormula'),
        value: latest?.bsaFormula ? getLabel(latest.bsaFormula, bsaFormulasOptions) : '-',
      },
      {
        label: t('characteristics.aibw'),
        value: latest?.aibw ? multipleDigitsNumberFormatter.format(latest?.aibw) : '-',
      },
      {
        label: t('characteristics.aibwFormula'),
        value: latest?.aibwFormula ? getLabel(latest.aibwFormula, ibwFormulasOptions) : '-',
      },
      {
        label: t('characteristics.useAibw'),
        value: latest?.useAibw ? t('yes') : t('no'),
      },
      {
        label: t('characteristics.diabetes'),
        value: latest?.diabetes ? t(`characteristics.diabetes.${latest.diabetes}`) : '-',
      },
      {
        label: t('characteristics.smoker'),
        value: latest?.smoker ? t('yes') : t('no'),
      },
      {
        label: t('characteristics.alcoholic'),
        value: latest?.alcoholic ? t('yes') : t('no'),
      },
    ],
    [t, latest]
  )

  return (
    <Box rounded="md" borderWidth="1px" padding="4">
      <DataSection
        label={
          <Box display="flex" flexDirection="row" justifyContent="space-between">
            <VStack align="flex-start">
              {t('diagnosis')}
              <Text fontSize="sm" color="fg.muted">
                {latest?.diagnosis?.name || `(${t('no_diagnosis_assigned')})`}
              </Text>
            </VStack>
            <Button
              variant="ghost"
              colorPalette="cyan"
              disabled={!canEditPatient}
              onClick={() => openPatientCharacteristicsDialog(patient.id)}
            >
              {t('edit')}
            </Button>
          </Box>
        }
      >
        <DataList items={items} />
      </DataSection>
    </Box>
  )
}

function getLabel(id: string, options: SelectItem[]) {
  const output = options.find(item => item.value === id)
  return output?.label
}
